import React, { useState, useEffect } from 'react';
import { Printer, Download, X, Bluetooth } from 'lucide-react';

const Receipt = ({ invoice, onClose, onPrint }) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const [isThermalPrinting, setIsThermalPrinting] = useState(false);
  const [bluetoothDevice, setBluetoothDevice] = useState(null);

  // Setup print event listeners for regular printing
  useEffect(() => {
    const handleAfterPrint = () => {
      setIsPrinting(false);
      if (onPrint) onPrint();
    };

    const handleBeforePrint = () => {
      setIsPrinting(true);
    };

    window.addEventListener('afterprint', handleAfterPrint);
    window.addEventListener('beforeprint', handleBeforePrint);

    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
      window.removeEventListener('beforeprint', handleBeforePrint);
    };
  }, [onPrint]);

  // ✅ ESC/POS Command Generator (NO CHANGES MADE)
  const generateESCPOSCommands = (invoice) => {
    const ESC = '\x1B';
    const GS = '\x1D';
    
    let commands = [];
    
    commands.push(ESC + '@'); 
    commands.push(ESC + 'a' + '\x01'); 
    
    commands.push(ESC + '!' + '\x30'); 
    commands.push(invoice.restaurantName + '\n');
    commands.push(ESC + '!' + '\x00'); 
    
    commands.push(ESC + 'a' + '\x01'); 
    commands.push(invoice.restaurantAddress + '\n');
    commands.push('Phone: ' + invoice.restaurantPhone + '\n');
    commands.push('GSTIN: ' + invoice.restaurantGSTIN + '\n');
    commands.push('--------------------------------\n');
    
    if (invoice.cancelled) {
      commands.push(ESC + '!' + '\x10'); 
      commands.push('*** CANCELLED ***\n');
      commands.push(new Date(invoice.cancelledAt).toLocaleString('en-IN') + '\n');
      commands.push('Reason: ' + invoice.cancellationReason + '\n');
      commands.push(ESC + '!' + '\x00'); 
      commands.push('--------------------------------\n');
    }
    
    commands.push(ESC + 'a' + '\x00'); 
    commands.push('Invoice: ' + invoice.invoiceNumber + '\n');
    commands.push('Date: ' + invoice.date + '\n');
    commands.push('Time: ' + invoice.time + '\n');
    commands.push('Table: ' + invoice.tableName + '\n');
    commands.push('Customer: ' + invoice.customerName + '\n');
    commands.push('Cashier: ' + invoice.cashier + '\n');
    commands.push('--------------------------------\n');
    
    commands.push(ESC + '!' + '\x08'); 
    commands.push('ITEMS\n');
    commands.push(ESC + '!' + '\x00'); 
    commands.push('--------------------------------\n');
    
    invoice.items.forEach(item => {
      const itemLine = item.name.substring(0, 20).padEnd(20, ' ');
      const qtyPrice = `${item.quantity}x${item.price}`.padStart(12, ' ');
      commands.push(itemLine + qtyPrice + '\n');
      
      const amountLine = `Rs.${item.amount}`.padStart(32, ' ');
      commands.push(amountLine + '\n');
    });
    
    commands.push('--------------------------------\n');
    
    commands.push('Subtotal:'.padEnd(20, ' ') + `Rs.${invoice.subtotal}`.padStart(12, ' ') + '\n');
    
    if (invoice.cgst > 0) {
      commands.push(`CGST(${invoice.cgstPercent}%):`.padEnd(20, ' ') + `Rs.${invoice.cgst}`.padStart(12, ' ') + '\n');
      commands.push(`SGST(${invoice.sgstPercent}%):`.padEnd(20, ' ') + `Rs.${invoice.sgst}`.padStart(12, ' ') + '\n');
    }
    
    if (invoice.serviceCharge > 0) {
      commands.push(`Service(${invoice.servicePercent}%):`.padEnd(20, ' ') + `Rs.${invoice.serviceCharge}`.padStart(12, ' ') + '\n');
    }
    
    commands.push('--------------------------------\n');
    
    commands.push(ESC + '!' + '\x30'); 
    commands.push('TOTAL: Rs.' + invoice.grandTotal + '\n');
    commands.push(ESC + '!' + '\x00'); 
    
    commands.push('--------------------------------\n');
    
    commands.push('Payment: ' + invoice.paymentMethod + '\n');
    commands.push('Status: ' + invoice.paymentStatus + '\n');
    commands.push('--------------------------------\n');
    
    commands.push(ESC + 'a' + '\x01'); 
    commands.push('THANK YOU FOR VISITING!\n');
    commands.push('Please visit again\n\n');
    
    commands.push(GS + 'V' + '\x41' + '\x03');
    
    return commands.join('');
  };

  // ✅ Connect and Print to Bluetooth Thermal Printer (NO CHANGES MADE)
  const handleThermalPrint = async () => {
    try {
      setIsThermalPrinting(true);

      if (!navigator.bluetooth) {
        alert('❌ Bluetooth is not supported in this browser. Please use Chrome or Edge on Android.');
        setIsThermalPrinting(false);
        return;
      }

      const device = await navigator.bluetooth.requestDevice({
        filters: [
          { services: ['000018f0-0000-1000-8000-00805f9b34fb'] }, 
          { namePrefix: 'POS' },
          { namePrefix: 'Niyama' },
          { namePrefix: 'RPP' },
          { namePrefix: 'BlueTooth Printer' }
        ],
        optionalServices: ['000018f0-0000-1000-8000-00805f9b34fb']
      });

      console.log('📱 Connecting to:', device.name);

      const server = await device.gatt.connect();
      console.log('✅ Connected to GATT server');

      const service = await server.getPrimaryService('000018f0-0000-1000-8000-00805f9b34fb');
      console.log('✅ Service obtained');

      const characteristic = await service.getCharacteristic('00002af1-0000-1000-8000-00805f9b34fb');
      console.log('✅ Characteristic obtained');

      const escposData = generateESCPOSCommands(invoice);
      
      const encoder = new TextEncoder();
      const data = encoder.encode(escposData);

      const chunkSize = 20;
      for (let i = 0; i < data.length; i += chunkSize) {
        const chunk = data.slice(i, i + chunkSize);
        await characteristic.writeValue(chunk);
        await new Promise(resolve => setTimeout(resolve, 50)); 
      }

      console.log('✅ Print job sent successfully!');
      alert('✅ Receipt printed successfully!');
      
      if (onPrint) onPrint();
      
      setBluetoothDevice(device);

    } catch (error) {
      console.error('❌ Bluetooth printing error:', error);
      
      if (error.name === 'NotFoundError') {
        alert('❌ No printer found. Make sure your Bluetooth printer is turned on and paired.');
      } else if (error.name === 'SecurityError') {
        alert('❌ Bluetooth access denied. Please enable Bluetooth permissions in browser settings.');
      } else {
        alert('❌ Printing failed: ' + error.message);
      }
    } finally {
      setIsThermalPrinting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const receiptText = generateTextReceipt(invoice);
    const blob = new Blob([receiptText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Receipt-${invoice.invoiceNumber}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const generateTextReceipt = (inv) => {
    let receipt = `
========================================
${inv.restaurantName.toUpperCase()}
========================================
${inv.restaurantAddress}
Phone: ${inv.restaurantPhone}
GSTIN: ${inv.restaurantGSTIN}
========================================

Invoice: ${inv.invoiceNumber}
Date: ${inv.date}
Time: ${inv.time}
Table: ${inv.tableName}
Customer: ${inv.customerName}
Cashier: ${inv.cashier}

========================================
ITEMS
========================================
${inv.items.map(item => 
  `${item.name}
  ${item.quantity} x Rs.${item.price} = Rs.${item.amount}`
).join('\n\n')}

========================================
BILL SUMMARY
========================================
Subtotal:              Rs.${inv.subtotal}
${inv.cgst > 0 ? `CGST (${inv.cgstPercent}%):        Rs.${inv.cgst}` : ''}
${inv.sgst > 0 ? `SGST (${inv.sgstPercent}%):        Rs.${inv.sgst}` : ''}
${inv.serviceCharge > 0 ? `Service (${inv.servicePercent}%):      Rs.${inv.serviceCharge}` : ''}
----------------------------------------
GRAND TOTAL:           Rs.${inv.grandTotal}
========================================

Payment Method: ${inv.paymentMethod}
Status: ${inv.paymentStatus}

${inv.cancelled ? `
CANCELLED - ${new Date(inv.cancelledAt).toLocaleString('en-IN')}
Reason: ${inv.cancellationReason}
` : ''}

========================================
THANK YOU FOR VISITING!
PLEASE VISIT AGAIN
========================================
    `.trim();
    return receipt;
  };

  return (
    <>
      {/* ✅ FIXED: Added style block to hide background and show only receipt during print */}
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            #printable-receipt, #printable-receipt * {
              visibility: visible;
            }
            #printable-receipt {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              margin: 0;
              padding: 0;
              background: white;
              box-shadow: none;
              border: none;
            }
          }
        `}
      </style>

      {/* Modal Overlay */}
      {/* ✅ FIXED: Removed 'print:hidden' class from here */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        {/* Action Buttons */}
        {/* ✅ FIXED: Added 'print:hidden' here so buttons don't print */}
        <div className="absolute top-4 right-4 flex gap-2 print:hidden">
          <button
            onClick={handleDownload}
            className="bg-blue-500 text-white p-3 rounded-lg hover:bg-blue-600 transition shadow-lg"
            title="Download Receipt"
          >
            <Download className="w-5 h-5" />
          </button>
          
          <button
            onClick={handleThermalPrint}
            disabled={isThermalPrinting}
            className="bg-purple-500 text-white p-3 rounded-lg hover:bg-purple-600 transition shadow-lg disabled:opacity-50 flex items-center gap-2"
            title="Print to Bluetooth Thermal Printer"
          >
            {isThermalPrinting ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Bluetooth className="w-5 h-5" />
            )}
          </button>
          
          <button
            onClick={handlePrint}
            disabled={isPrinting}
            className="bg-green-500 text-white p-3 rounded-lg hover:bg-green-600 transition shadow-lg disabled:opacity-50"
            title="Print Receipt (Regular Printer)"
          >
            <Printer className="w-5 h-5" />
          </button>
          
          <button
            onClick={onClose}
            className="bg-red-500 text-white p-3 rounded-lg hover:bg-red-600 transition shadow-lg"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Receipt Display */}
        {/* ✅ FIXED: Added id="printable-receipt" for CSS targeting */}
        <div id="printable-receipt" className="bg-white w-full max-w-md max-h-[90vh] overflow-y-auto rounded-lg shadow-2xl">
          <div className="p-8">
            {invoice.cancelled && (
              <div className="bg-red-100 border-2 border-red-500 rounded-lg p-3 mb-4 text-center">
                <p className="text-red-700 font-bold text-lg">CANCELLED</p>
                <p className="text-red-600 text-sm mt-1">
                  {new Date(invoice.cancelledAt).toLocaleString('en-IN')}
                </p>
                <p className="text-red-600 text-xs mt-1">
                  Reason: {invoice.cancellationReason}
                </p>
              </div>
            )}

            <div className="text-center border-b-2 border-dashed border-gray-300 pb-4 mb-4">
              <h1 className="text-2xl font-bold text-gray-800 mb-1">{invoice.restaurantName}</h1>
              <p className="text-sm text-gray-600">{invoice.restaurantAddress}</p>
              <p className="text-sm text-gray-600">📞 {invoice.restaurantPhone}</p>
              <p className="text-xs text-gray-500 mt-1">GSTIN: {invoice.restaurantGSTIN}</p>
            </div>

            <div className="border-b border-gray-300 pb-4 mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="font-semibold">Invoice No:</span>
                <span className="font-mono">{invoice.invoiceNumber}</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-semibold">Date:</span>
                <span>{invoice.date}</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-semibold">Time:</span>
                <span>{invoice.time}</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-semibold">Table:</span>
                <span>{invoice.tableName}</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-semibold">Customer:</span>
                <span>{invoice.customerName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-semibold">Cashier:</span>
                <span>{invoice.cashier}</span>
              </div>
            </div>

            <div className="border-b border-gray-300 pb-4 mb-4">
              <h3 className="font-bold text-gray-800 mb-3">ITEMS</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2">Item</th>
                    <th className="text-center py-2">Qty</th>
                    <th className="text-right py-2">Price</th>
                    <th className="text-right py-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((item, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="py-2 text-left">{item.name}</td>
                      <td className="py-2 text-center">{item.quantity}</td>
                      <td className="py-2 text-right">₹{item.price}</td>
                      <td className="py-2 text-right font-semibold">₹{item.amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="border-b-2 border-dashed border-gray-300 pb-4 mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span>Subtotal:</span>
                <span className="font-semibold">₹{invoice.subtotal}</span>
              </div>
              
              {(invoice.cgst > 0 || invoice.sgst > 0) && (
                <>
                  <div className="flex justify-between text-sm mb-2 text-gray-600">
                    <span>CGST ({invoice.cgstPercent}%):</span>
                    <span>₹{invoice.cgst}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2 text-gray-600">
                    <span>SGST ({invoice.sgstPercent}%):</span>
                    <span>₹{invoice.sgst}</span>
                  </div>
                </>
              )}
              
              {invoice.serviceCharge > 0 && (
                <div className="flex justify-between text-sm mb-2 text-gray-600">
                  <span>Service Charge ({invoice.servicePercent}%):</span>
                  <span>₹{invoice.serviceCharge}</span>
                </div>
              )}
              
              <div className="flex justify-between text-lg font-bold mt-3 pt-3 border-t-2 border-gray-300">
                <span>GRAND TOTAL:</span>
                <span className="text-orange-600">₹{invoice.grandTotal}</span>
              </div>
            </div>

            <div className="border-b border-gray-300 pb-4 mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="font-semibold">Payment Method:</span>
                <span>{invoice.paymentMethod}</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-semibold">Status:</span>
                <span className="text-green-600 font-bold">{invoice.paymentStatus}</span>
              </div>
            </div>

            <div className="text-center text-sm text-gray-600">
              <p className="font-bold mb-2">THANK YOU FOR VISITING!</p>
              <p className="mb-1">Please visit again</p>
              <p className="text-xs text-gray-500 mt-3">
                This is a computer-generated receipt
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Receipt;